import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { adminAPI, uploadFile, downloadFile } from '../../utils/api';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Edit2, Trash2, Upload, Users, Zap, UserCheck, Phone, MapPin, X, Search, Download, MessageSquare, Calendar, FileSpreadsheet, IndianRupee, ChevronLeft, ChevronRight, Loader2, Filter, RefreshCw } from 'lucide-react';

// Format month as "April 2026"
const formatMonth = (monthStr) => {
  if (!monthStr) return '-';
  const date = new Date(monthStr + '-01');
  return date.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
};

const ZoneStaffManagement = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [filteredStaff, setFilteredStaff] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [regions, setRegions] = useState([]);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('staff'); // 'staff' or 'report'
  
  // Monthly Report states
  const [reportMonth, setReportMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [reportCategoryFilter, setReportCategoryFilter] = useState('');
  const [reportRegionFilter, setReportRegionFilter] = useState('');
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportDownloading, setReportDownloading] = useState(false);
  const [downloadElapsed, setDownloadElapsed] = useState(0);
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    mobile_number: '',
    mobile_number_2: '',
    category: 'electrician',
    assigned_zones: [],
    assigned_divisions: [],
    assigned_parks: [],
    salary_per_day: ''
  });
  
  // Parks list for assignment (only for supervisors)
  const [parksList, setParksList] = useState([]);
  const [parksLoading, setParksLoading] = useState(false);
  
  // Upload states
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  const [whatsAppRegion, setWhatsAppRegion] = useState('north');
  
  const { user, userType, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [hasFetched, setHasFetched] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user || userType !== 'admin') {
      navigate('/admin/login');
    }
  }, [user, userType, authLoading, navigate]);

  useEffect(() => {
    if (user && userType === 'admin' && !hasFetched) {
      fetchStaff();
      setHasFetched(true);
    }
  }, [user, userType, hasFetched]);

  useEffect(() => {
    filterStaff();
  }, [staffList, categoryFilter, regionFilter, searchTerm]);

  // Fetch report when tab is switched to report (only once per tab switch)
  const [reportFetched, setReportFetched] = useState(false);
  
  useEffect(() => {
    if (activeTab === 'report' && user && userType === 'admin' && !reportFetched) {
      fetchMonthlyReport();
      setReportFetched(true);
    }
    if (activeTab !== 'report') {
      setReportFetched(false); // Reset when leaving tab
    }
  }, [activeTab, user, userType]);
  
  // Refetch report when filters change (only if on report tab)
  const handleReportFilterChange = () => {
    if (activeTab === 'report') {
      fetchMonthlyReport();
    }
  };

  // Download elapsed time timer
  useEffect(() => {
    let interval;
    if (reportDownloading) {
      interval = setInterval(() => {
        setDownloadElapsed(prev => prev + 1);
      }, 1000);
    } else {
      setDownloadElapsed(0);
    }
    return () => clearInterval(interval);
  }, [reportDownloading]);

  const fetchMonthlyReport = async () => {
    setReportLoading(true);
    try {
      const response = await adminAPI.getZoneStaffMonthlyReport(
        reportMonth,
        reportCategoryFilter || null,
        reportRegionFilter || null
      );
      setReportData(response.data);
    } catch (error) {
      toast.error('Failed to load monthly report');
    } finally {
      setReportLoading(false);
    }
  };

  const handleDownloadReport = async () => {
    setReportDownloading(true);
    const monthName = formatMonth(reportMonth);
    const regionLabel = reportRegionFilter ? reportRegionFilter.toUpperCase() : 'All';
    const categoryLabel = reportCategoryFilter ? reportCategoryFilter.charAt(0).toUpperCase() + reportCategoryFilter.slice(1) : 'All';
    const filename = `Zone_Staff_Report_${regionLabel}_${categoryLabel}_${monthName.replace(' ', '_')}.xlsx`;
    
    await downloadFile(
      adminAPI.downloadZoneStaffMonthlyReport(
        reportMonth,
        reportCategoryFilter || null,
        reportRegionFilter || null
      ),
      filename,
      () => toast.success('Report downloaded successfully'),
      (error) => toast.error(error || 'Failed to download report')
    );
    setReportDownloading(false);
  };

  const getMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = formatMonth(value);
      options.push({ value, label });
    }
    return options;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
  };

  const formatElapsedTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  // Validate mobile number - must be exactly 10 digits
  const isValidMobile = (mobile) => {
    if (!mobile) return false;
    const digitsOnly = mobile.replace(/\D/g, '');
    return digitsOnly.length === 10;
  };

  const fetchStaff = async (isManualRefresh = false) => {
    if (isManualRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const response = await adminAPI.getZoneStaff();
      setStaffList(response.data.staff || []);
      setRegions(response.data.regions || []);
      if (isManualRefresh) {
        toast.success('Data refreshed');
      }
    } catch (error) {
      toast.error('Failed to load zone staff');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    if (activeTab === 'report') {
      fetchMonthlyReport();
    } else {
      fetchStaff(true);
    }
  };

  const filterStaff = () => {
    let filtered = [...staffList];
    
    if (categoryFilter) {
      filtered = filtered.filter(s => s.category?.toLowerCase() === categoryFilter.toLowerCase());
    }
    
    if (regionFilter) {
      filtered = filtered.filter(s => s.region?.toLowerCase() === regionFilter.toLowerCase());
    }
    
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(s => 
        s.name?.toLowerCase().includes(search) ||
        s.employee_id?.toLowerCase().includes(search) ||
        s.mobile_number?.includes(search)
      );
    }
    
    // Sort by zone, then by name
    filtered.sort((a, b) => {
      const zoneA = a.assigned_zones?.[0] || '99';
      const zoneB = b.assigned_zones?.[0] || '99';
      if (zoneA !== zoneB) return parseInt(zoneA) - parseInt(zoneB);
      return (a.name || '').localeCompare(b.name || '');
    });
    
    setFilteredStaff(filtered);
  };

  // Group staff by zone for display
  const getStaffByZone = () => {
    const grouped = {};
    filteredStaff.forEach(staff => {
      const zone = staff.assigned_zones?.[0] || 'Unassigned';
      if (!grouped[zone]) {
        grouped[zone] = [];
      }
      grouped[zone].push(staff);
    });
    return grouped;
  };

  const handleAddStaff = async () => {
    if (!formData.name || !formData.category) {
      toast.error('Name and Category are required');
      return;
    }
    
    try {
      await adminAPI.createZoneStaff(formData);
      toast.success('Staff added successfully');
      setShowAddModal(false);
      resetForm();
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add staff');
    }
  };

  const handleUpdateStaff = async () => {
    if (!selectedStaff) return;
    
    try {
      await adminAPI.updateZoneStaff(selectedStaff.id, formData);
      toast.success('Staff updated successfully');
      setShowEditModal(false);
      setSelectedStaff(null);
      resetForm();
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update staff');
    }
  };

  const handleDeleteStaff = async (staff) => {
    if (!window.confirm(`Are you sure you want to delete ${staff.name}?`)) return;
    
    try {
      await adminAPI.deleteZoneStaff(staff.id);
      toast.success('Staff deleted successfully');
      fetchStaff();
    } catch (error) {
      toast.error('Failed to delete staff');
    }
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    setUploadProgress(0);
    setUploadResult(null);
    
    const progressInterval = setInterval(() => {
      setUploadProgress(prev => prev >= 90 ? prev : prev + Math.random() * 15);
    }, 300);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await adminAPI.uploadZoneStaffExcel(formData);
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      setUploadResult(response.data);
      
      if (response.data.created_count > 0 || response.data.updated_count > 0) {
        toast.success(`Created: ${response.data.created_count}, Updated: ${response.data.updated_count}`);
        fetchStaff();
      }
    } catch (error) {
      clearInterval(progressInterval);
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleWhatsAppUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Prevent duplicate uploads
    if (uploading) {
      console.log('Upload already in progress, ignoring');
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    setUploadResult(null);
    
    const progressInterval = setInterval(() => {
      setUploadProgress(prev => prev >= 90 ? prev : prev + Math.random() * 10);
    }, 300);
    
    const formData = new FormData();
    formData.append('file', file);
    
    const result = await uploadFile(
      adminAPI.uploadWhatsAppAttendance(formData, whatsAppRegion),
      (data) => {
        // Success handler
        clearInterval(progressInterval);
        setUploadProgress(100);
        setUploadResult(data);
        
        const markedCount = data.marked_count ?? 0;
        const skippedCount = data.skipped_count ?? 0;
        
        if (markedCount > 0) {
          toast.success(`✓ Marked ${markedCount} NEW attendance records`);
        } else if (skippedCount > 0) {
          toast.info(`No new records - ${skippedCount} already exist`);
        } else {
          toast.warning('No matching staff found in the uploaded file');
        }
      },
      (errorMessage) => {
        // Error handler
        clearInterval(progressInterval);
        toast.error(errorMessage);
      }
    );
    
    if (!result.success && !result.data) {
      clearInterval(progressInterval);
    }
    
    setUploading(false);
    e.target.value = '';
  };

  const openEditModal = async (staff) => {
    setSelectedStaff(staff);
    setFormData({
      name: staff.name || '',
      mobile_number: staff.mobile_number || '',
      mobile_number_2: staff.mobile_number_2 || '',
      category: staff.category || 'electrician',
      assigned_zones: staff.assigned_zones || [],
      assigned_divisions: staff.assigned_divisions || [],
      assigned_parks: staff.assigned_parks || [],
      salary_per_day: staff.salary_per_day || ''
    });
    setShowEditModal(true);
    
    // Fetch parks for supervisor assignment (pass current staff's assigned parks)
    if (staff.category === 'supervisor') {
      await fetchParksForAssignment(staff.assigned_zones, staff.assigned_divisions, staff.assigned_parks || []);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      mobile_number: '',
      mobile_number_2: '',
      category: 'electrician',
      assigned_zones: [],
      assigned_divisions: [],
      assigned_parks: [],
      salary_per_day: ''
    });
    setParksList([]);
  };

  const fetchParksForAssignment = async (zones, divisions, currentStaffParks = []) => {
    setParksLoading(true);
    try {
      const response = await adminAPI.getParks();
      let parks = response.data.parks || [];
      
      // Get parks already assigned to other supervisors
      const currentStaffId = selectedStaff?.id;
      const assignedByOthers = new Set();
      staffList.forEach(staff => {
        if (staff.category === 'supervisor' && staff.id !== currentStaffId) {
          (staff.assigned_parks || []).forEach(parkId => assignedByOthers.add(parkId));
        }
      });
      
      // Filter: keep parks that are either:
      // 1. Currently assigned to this staff (regardless of zone/division filter)
      // 2. Match zone/division filter AND not assigned to others
      const currentParkIds = new Set(currentStaffParks);
      
      parks = parks.filter(p => {
        // Always keep current staff's assigned parks
        if (currentParkIds.has(p.park_id)) return true;
        
        // Exclude parks assigned to others
        if (assignedByOthers.has(p.park_id)) return false;
        
        // If zone/division filters specified, apply them
        if (zones && zones.length > 0 && !zones.includes(p.zone)) return false;
        if (divisions && divisions.length > 0 && !divisions.includes(p.division)) return false;
        
        return true;
      });
      
      setParksList(parks);
    } catch (error) {
      console.error('Failed to fetch parks');
    } finally {
      setParksLoading(false);
    }
  };

  const handleZonesChange = (e) => {
    const zones = e.target.value.split(',').map(z => z.trim()).filter(z => z);
    setFormData({ ...formData, assigned_zones: zones });
    // Refresh parks if supervisor
    if (formData.category === 'supervisor') {
      fetchParksForAssignment(zones, formData.assigned_divisions, formData.assigned_parks || []);
    }
  };

  const handleDivisionsChange = (e) => {
    const divisions = e.target.value.split(',').map(d => d.trim()).filter(d => d);
    setFormData({ ...formData, assigned_divisions: divisions });
    // Refresh parks if supervisor
    if (formData.category === 'supervisor') {
      fetchParksForAssignment(formData.assigned_zones, divisions, formData.assigned_parks || []);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <div className="bg-slate-800 text-white p-4 sticky top-0 z-20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard" className="p-2 hover:bg-slate-700 rounded-lg">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <h1 className="text-xl font-bold">ZONE STAFF MANAGEMENT</h1>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing || loading || reportLoading}
            className="h-10 px-4 rounded-lg bg-white/20 hover:bg-white/30 disabled:opacity-50 flex items-center gap-2"
            data-testid="refresh-zone-staff-btn"
          >
            <RefreshCw className={`w-5 h-5 ${refreshing || reportLoading ? 'animate-spin' : ''}`} />
            {refreshing || reportLoading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
        {/* Tab Navigation */}
        <div className="flex gap-2 mt-3">
          <button
            onClick={() => setActiveTab('staff')}
            className={`px-4 py-2 rounded-lg font-bold text-sm transition-colors ${
              activeTab === 'staff' 
                ? 'bg-white text-slate-800' 
                : 'bg-slate-700 text-white hover:bg-slate-600'
            }`}
            data-testid="tab-staff-list"
          >
            <Users className="w-4 h-4 inline mr-2" />
            Staff List
          </button>
          <button
            onClick={() => setActiveTab('report')}
            className={`px-4 py-2 rounded-lg font-bold text-sm transition-colors ${
              activeTab === 'report' 
                ? 'bg-white text-slate-800' 
                : 'bg-slate-700 text-white hover:bg-slate-600'
            }`}
            data-testid="tab-monthly-report"
          >
            <FileSpreadsheet className="w-4 h-4 inline mr-2" />
            Monthly Report
          </button>
        </div>
      </div>

      {/* Staff List Tab Content */}
      {activeTab === 'staff' && (
      <div className="p-4 max-w-7xl mx-auto">
        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 mb-6">
          <button
            onClick={() => { resetForm(); setShowAddModal(true); }}
            className="h-12 px-6 bg-green-600 hover:bg-green-700 text-white rounded-lg font-bold flex items-center gap-2"
            data-testid="add-staff-btn"
          >
            <Plus className="w-5 h-5" /> Add Staff
          </button>
          <button
            onClick={() => { setUploadResult(null); setShowUploadModal(true); }}
            className="h-12 px-6 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold flex items-center gap-2"
            data-testid="upload-excel-btn"
          >
            <Upload className="w-5 h-5" /> Upload Excel
          </button>
          <button
            onClick={() => { setUploadResult(null); setShowWhatsAppModal(true); }}
            className="h-12 px-6 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold flex items-center gap-2"
            data-testid="whatsapp-upload-btn"
          >
            <MessageSquare className="w-5 h-5" /> Upload WhatsApp Attendance
          </button>
          <Link
            to="/admin/zone-staff-attendance"
            className="h-12 px-6 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-bold flex items-center gap-2"
            data-testid="view-attendance-btn"
          >
            <UserCheck className="w-5 h-5" /> View Attendance Calendar
          </Link>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{staffList.length}</div>
                <div className="text-sm text-slate-500">Total Staff</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Zap className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{staffList.filter(s => s.category === 'electrician').length}</div>
                <div className="text-sm text-slate-500">Electricians</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <UserCheck className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{staffList.filter(s => s.category === 'supervisor').length}</div>
                <div className="text-sm text-slate-500">Supervisors</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <MapPin className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold text-green-600">
                    {staffList.filter(s => s.category === 'supervisor' && (s.assigned_parks?.length || 0) > 0).length}
                  </span>
                  <span className="text-slate-400">/</span>
                  <span className="text-lg font-bold text-red-600">
                    {staffList.filter(s => s.category === 'supervisor' && (s.assigned_parks?.length || 0) === 0).length}
                  </span>
                </div>
                <div className="text-xs text-slate-500">Supervisors with Parks / Without Parks</div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by name, ID, or mobile..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full h-10 pl-10 pr-4 rounded-lg border-2 border-slate-300 focus:border-blue-500 focus:ring-0"
                  data-testid="search-input"
                />
              </div>
            </div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="h-10 px-4 rounded-lg border-2 border-slate-300"
              data-testid="category-filter"
            >
              <option value="">All Categories</option>
              <option value="electrician">Electrician</option>
              <option value="supervisor">Supervisor</option>
            </select>
            <select
              value={regionFilter}
              onChange={(e) => setRegionFilter(e.target.value)}
              className="h-10 px-4 rounded-lg border-2 border-slate-300"
              data-testid="region-filter"
            >
              <option value="">All Regions</option>
              <option value="north">North (Zones 1-5)</option>
              <option value="central">Central (Zones 6-10)</option>
              <option value="south">South (Zones 11-15)</option>
            </select>
          </div>
        </div>

        {/* Warning for Invalid Mobile Numbers */}
        {staffList.filter(s => !isValidMobile(s.mobile_number)).length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2">
              <span className="text-red-600 font-bold text-lg">⚠</span>
              <div>
                <p className="font-bold text-red-800">
                  {staffList.filter(s => !isValidMobile(s.mobile_number)).length} staff with invalid/missing mobile numbers
                </p>
                <p className="text-sm text-red-600">
                  WhatsApp attendance parsing requires exactly 10-digit mobile numbers. Fix highlighted entries to ensure accurate attendance tracking.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Staff List - Grouped by Zone */}
        <div className="space-y-4">
          {Object.entries(getStaffByZone()).map(([zone, zoneStaff]) => (
            <div key={zone} className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="p-3 border-b bg-gradient-to-r from-slate-100 to-slate-50 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-slate-600" />
                  <h3 className="font-bold text-slate-800">
                    {zone === 'Unassigned' ? 'Unassigned Zone' : `Zone ${zone}`}
                  </h3>
                  <span className={`ml-2 px-2 py-0.5 rounded text-xs font-bold ${
                    parseInt(zone) >= 1 && parseInt(zone) <= 5 ? 'bg-blue-100 text-blue-700' :
                    parseInt(zone) >= 6 && parseInt(zone) <= 10 ? 'bg-purple-100 text-purple-700' :
                    parseInt(zone) >= 11 && parseInt(zone) <= 15 ? 'bg-orange-100 text-orange-700' :
                    'bg-slate-100 text-slate-600'
                  }`}>
                    {parseInt(zone) >= 1 && parseInt(zone) <= 5 ? 'NORTH' :
                     parseInt(zone) >= 6 && parseInt(zone) <= 10 ? 'CENTRAL' :
                     parseInt(zone) >= 11 && parseInt(zone) <= 15 ? 'SOUTH' : '-'}
                  </span>
                </div>
                <span className="text-sm text-slate-500">{zoneStaff.length} staff</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full table-fixed">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="p-2 text-left text-xs font-bold text-slate-500 w-20">ID</th>
                      <th className="p-2 text-left text-xs font-bold text-slate-500 w-40">Name</th>
                      <th className="p-2 text-left text-xs font-bold text-slate-500 w-24">Category</th>
                      <th className="p-2 text-left text-xs font-bold text-slate-500 w-28">Parks</th>
                      <th className="p-2 text-left text-xs font-bold text-slate-500 w-32">Mobile</th>
                      <th className="p-2 text-center text-xs font-bold text-slate-500 w-20">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {zoneStaff.map((staff) => (
                      <tr key={staff.id} className="border-b hover:bg-slate-50 h-14">
                        <td className="p-2 text-sm font-mono text-slate-600 truncate">{staff.employee_id}</td>
                        <td className="p-2 font-semibold truncate">{staff.name}</td>
                        <td className="p-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                            staff.category === 'electrician' 
                              ? 'bg-yellow-100 text-yellow-700' 
                              : 'bg-green-100 text-green-700'
                          }`}>
                            {staff.category === 'electrician' ? 'Electrician' : 'Supervisor'}
                          </span>
                        </td>
                        <td className="p-2">
                          {staff.category === 'supervisor' ? (
                            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                              (staff.assigned_parks?.length || 0) > 0 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-red-100 text-red-700'
                            }`}>
                              {staff.assigned_parks?.length || 0} parks
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400">N/A</span>
                          )}
                        </td>
                        <td className="p-2 text-sm">
                          <div className="space-y-1">
                            {/* Primary Mobile */}
                            {staff.mobile_number ? (
                              <div className={`${
                                !isValidMobile(staff.mobile_number) 
                                  ? 'text-red-600 font-bold bg-red-50 px-2 py-0.5 rounded' 
                                  : ''
                              }`} title={!isValidMobile(staff.mobile_number) ? 'Invalid: Mobile must be exactly 10 digits' : ''}>
                                {staff.mobile_number}
                                {!isValidMobile(staff.mobile_number) && (
                                  <span className="ml-1 text-xs">⚠</span>
                                )}
                              </div>
                            ) : (
                              <div className="text-red-500 font-bold bg-red-50 px-2 py-0.5 rounded inline-block">
                                Not set ⚠
                              </div>
                            )}
                            {/* Secondary Mobile */}
                            {staff.mobile_number_2 && (
                              <div className={`text-xs ${
                                !isValidMobile(staff.mobile_number_2) 
                                  ? 'text-red-500 bg-red-50 px-2 py-0.5 rounded' 
                                  : 'text-slate-500'
                              }`} title={!isValidMobile(staff.mobile_number_2) ? 'Invalid: Mobile 2 must be exactly 10 digits' : 'Secondary number'}>
                                +{staff.mobile_number_2}
                                {!isValidMobile(staff.mobile_number_2) && <span className="ml-1">⚠</span>}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="p-2">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => openEditModal(staff)}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                              data-testid={`edit-${staff.id}`}
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteStaff(staff)}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                              data-testid={`delete-${staff.id}`}
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
          
          {filteredStaff.length === 0 && (
            <div className="bg-white rounded-lg shadow-sm p-8 text-center text-slate-500">
              No staff found. Add staff or upload Excel to get started.
            </div>
          )}
        </div>
      </div>
      )}

      {/* Monthly Report Tab Content */}
      {activeTab === 'report' && (
      <div className="p-4 max-w-7xl mx-auto">
        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            {/* Month Selector */}
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-bold text-slate-700 mb-1">Month</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <select
                  value={reportMonth}
                  onChange={(e) => setReportMonth(e.target.value)}
                  className="w-full h-12 pl-10 pr-4 border-2 border-slate-300 rounded-lg focus:border-indigo-600 focus:ring-0 appearance-none bg-white"
                  data-testid="report-month-select"
                >
                  {getMonthOptions().map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Category Filter */}
            <div className="flex-1 min-w-[150px]">
              <label className="block text-sm font-bold text-slate-700 mb-1">Category</label>
              <select
                value={reportCategoryFilter}
                onChange={(e) => setReportCategoryFilter(e.target.value)}
                className="w-full h-12 px-4 border-2 border-slate-300 rounded-lg focus:border-indigo-600 focus:ring-0"
                data-testid="report-category-filter"
              >
                <option value="">All Categories</option>
                <option value="electrician">Electrician</option>
                <option value="supervisor">Supervisor</option>
              </select>
            </div>
            
            {/* Region Filter */}
            <div className="flex-1 min-w-[150px]">
              <label className="block text-sm font-bold text-slate-700 mb-1">Region</label>
              <select
                value={reportRegionFilter}
                onChange={(e) => setReportRegionFilter(e.target.value)}
                className="w-full h-12 px-4 border-2 border-slate-300 rounded-lg focus:border-indigo-600 focus:ring-0"
                data-testid="report-region-filter"
              >
                <option value="">All Regions</option>
                <option value="north">North (1-5)</option>
                <option value="central">Central (6-10)</option>
                <option value="south">South (11-15)</option>
              </select>
            </div>
            
            {/* Load Report Button */}
            <div className="flex items-end">
              <button
                onClick={fetchMonthlyReport}
                disabled={reportLoading}
                className="h-12 px-6 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 text-white rounded-lg font-bold flex items-center gap-2"
                data-testid="load-report-btn"
              >
                {reportLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Filter className="w-5 h-5" />
                )}
                Load Report
              </button>
            </div>
            
            {/* Download Button */}
            <div className="flex items-end">
              <button
                onClick={handleDownloadReport}
                disabled={reportDownloading || reportLoading || !reportData?.data?.length}
                className="h-12 px-6 bg-green-600 hover:bg-green-700 disabled:bg-slate-300 text-white rounded-lg font-bold flex items-center gap-2"
                data-testid="download-report-btn"
              >
                {reportDownloading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Download className="w-5 h-5" />
                )}
                Download Excel
              </button>
            </div>
          </div>
          
          {/* Download Progress Bar */}
          {reportDownloading && (
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                  <span className="text-sm font-semibold text-blue-800">
                    Generating report... Please wait
                  </span>
                </div>
                <span className="text-sm text-blue-600">
                  {formatElapsedTime(downloadElapsed)} elapsed
                </span>
              </div>
              <div className="w-full bg-blue-100 rounded-full h-3">
                <div 
                  className="bg-blue-600 h-3 rounded-full transition-all duration-300 animate-pulse"
                  style={{ width: '70%' }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Salary Rates Info */}
        {reportData?.salary_rates && (
          <div className={`rounded-xl p-4 mb-6 ${
            (reportData.salary_rates.electrician_salary_per_day === 0 || reportData.salary_rates.supervisor_salary_per_day === 0)
              ? 'bg-red-50 border border-red-200'
              : 'bg-green-50 border border-green-200'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              <IndianRupee className={`w-5 h-5 ${
                (reportData.salary_rates.electrician_salary_per_day === 0 || reportData.salary_rates.supervisor_salary_per_day === 0)
                  ? 'text-red-600'
                  : 'text-green-600'
              }`} />
              <h3 className={`font-bold ${
                (reportData.salary_rates.electrician_salary_per_day === 0 || reportData.salary_rates.supervisor_salary_per_day === 0)
                  ? 'text-red-800'
                  : 'text-green-800'
              }`}>Current Salary Rates (per day)</h3>
              {(reportData.salary_rates.electrician_salary_per_day === 0 || reportData.salary_rates.supervisor_salary_per_day === 0) && (
                <span className="text-red-600 font-bold">⚠ Not configured!</span>
              )}
            </div>
            <div className="flex flex-wrap gap-6 text-sm">
              <span className={reportData.salary_rates.electrician_salary_per_day === 0 ? 'text-red-700' : 'text-green-700'}>
                Electrician: <strong className={reportData.salary_rates.electrician_salary_per_day === 0 ? 'text-red-600' : ''}>
                  ₹{reportData.salary_rates.electrician_salary_per_day || 0}
                </strong>
              </span>
              <span className={reportData.salary_rates.supervisor_salary_per_day === 0 ? 'text-red-700' : 'text-green-700'}>
                Supervisor: <strong className={reportData.salary_rates.supervisor_salary_per_day === 0 ? 'text-red-600' : ''}>
                  ₹{reportData.salary_rates.supervisor_salary_per_day || 0}
                </strong>
              </span>
            </div>
            <p className={`text-xs mt-2 ${
              (reportData.salary_rates.electrician_salary_per_day === 0 || reportData.salary_rates.supervisor_salary_per_day === 0)
                ? 'text-red-600 font-semibold'
                : 'text-green-600'
            }`}>
              Configure salary rates in <Link to="/admin/settings" className="underline font-semibold">Admin Settings</Link>
              {(reportData.salary_rates.electrician_salary_per_day === 0 || reportData.salary_rates.supervisor_salary_per_day === 0) && 
                ' to calculate accurate salaries'}
            </p>
          </div>
        )}

        {/* Summary Cards */}
        {reportData && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl shadow-sm p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <div className="text-xl font-bold">{reportData.total_staff}</div>
                  <div className="text-xs text-slate-500">Total Staff</div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <Zap className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <div className="text-xl font-bold">{reportData.total_electricians}</div>
                  <div className="text-xs text-slate-500">Electricians</div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <UserCheck className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <div className="text-xl font-bold">{reportData.total_supervisors}</div>
                  <div className="text-xs text-slate-500">Supervisors</div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <IndianRupee className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <div className="text-xl font-bold">{formatCurrency(reportData.total_salary)}</div>
                  <div className="text-xs text-slate-500">Total Salary</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Data Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="bg-slate-50 p-4 border-b">
            <h3 className="font-bold text-lg">Monthly Report Data</h3>
            <p className="text-sm text-slate-500">Grouped by Zone. Download Excel for full data.</p>
          </div>
          
          {reportLoading ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="mt-4 text-slate-600">Loading report data...</p>
            </div>
          ) : reportData?.data?.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FileSpreadsheet className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p className="font-semibold">No data found for this period</p>
              <p className="text-sm">Try selecting a different month or filter</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left p-3 font-bold">Zone</th>
                    <th className="text-left p-3 font-bold">Region</th>
                    <th className="text-left p-3 font-bold">Staff Name</th>
                    <th className="text-left p-3 font-bold">Category</th>
                    <th className="text-center p-3 font-bold">Days Present</th>
                    <th className="text-right p-3 font-bold">Salary Payable</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData?.data?.map((row, idx) => (
                    <tr key={idx} className="border-t hover:bg-slate-50">
                      <td className="p-3 font-medium">{row.zone}</td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                          row.region === 'north' ? 'bg-blue-100 text-blue-700' :
                          row.region === 'central' ? 'bg-purple-100 text-purple-700' :
                          row.region === 'south' ? 'bg-orange-100 text-orange-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {row.region?.toUpperCase()}
                        </span>
                      </td>
                      <td className="p-3">{row.name}</td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                          row.category?.toLowerCase() === 'electrician' ? 'bg-yellow-100 text-yellow-700' :
                          row.category?.toLowerCase() === 'supervisor' ? 'bg-green-100 text-green-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {row.category}
                        </span>
                      </td>
                      <td className="p-3 text-center font-bold">{row.days_present}</td>
                      <td className="p-3 text-right font-bold text-green-600">{formatCurrency(row.salary_payable)}</td>
                    </tr>
                  ))}
                </tbody>
                {reportData?.data?.length > 0 && (
                  <tfoot className="bg-slate-100 font-bold">
                    <tr>
                      <td colSpan="4" className="p-3 text-right">TOTAL</td>
                      <td className="p-3 text-center">{reportData.data.reduce((sum, r) => sum + r.days_present, 0)}</td>
                      <td className="p-3 text-right text-green-600">{formatCurrency(reportData.total_salary)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl w-full max-w-md my-auto max-h-[90vh] flex flex-col">
            <div className="p-4 border-b flex items-center justify-between flex-shrink-0">
              <h2 className="font-bold text-lg">{showAddModal ? 'Add New Staff' : 'Edit Staff'}</h2>
              <button onClick={() => { setShowAddModal(false); setShowEditModal(false); }} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="text-sm font-bold text-slate-700 mb-1 block">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full h-10 px-3 rounded-lg border-2 border-slate-300 focus:border-blue-500 focus:ring-0"
                  placeholder="Enter full name"
                  data-testid="staff-name-input"
                />
              </div>
              <div>
                <label className="text-sm font-bold text-slate-700 mb-1 block">Mobile Number</label>
                <input
                  type="text"
                  value={formData.mobile_number}
                  onChange={(e) => setFormData({ ...formData, mobile_number: e.target.value })}
                  className="w-full h-10 px-3 rounded-lg border-2 border-slate-300 focus:border-blue-500 focus:ring-0"
                  placeholder="10-digit mobile number"
                  data-testid="staff-mobile-input"
                />
              </div>
              <div>
                <label className="text-sm font-bold text-slate-700 mb-1 block">
                  Mobile Number 2 <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={formData.mobile_number_2}
                  onChange={(e) => setFormData({ ...formData, mobile_number_2: e.target.value })}
                  className="w-full h-10 px-3 rounded-lg border-2 border-slate-300 focus:border-blue-500 focus:ring-0"
                  placeholder="Alternate 10-digit mobile number"
                  data-testid="staff-mobile2-input"
                />
                <p className="text-xs text-slate-500 mt-1">WhatsApp attendance will be captured from either number</p>
              </div>
              <div>
                <label className="text-sm font-bold text-slate-700 mb-1 block">Category *</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full h-10 px-3 rounded-lg border-2 border-slate-300 focus:border-blue-500 focus:ring-0"
                  data-testid="staff-category-select"
                >
                  <option value="electrician">Electrician</option>
                  <option value="supervisor">Supervisor</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-bold text-slate-700 mb-1 block">Assigned Zones (comma separated)</label>
                <input
                  type="text"
                  value={formData.assigned_zones?.join(', ')}
                  onChange={handleZonesChange}
                  className="w-full h-10 px-3 rounded-lg border-2 border-slate-300 focus:border-blue-500 focus:ring-0"
                  placeholder="e.g., 1, 2, 3"
                  data-testid="staff-zones-input"
                />
              </div>
              <div>
                <label className="text-sm font-bold text-slate-700 mb-1 block">Assigned Divisions (comma separated)</label>
                <input
                  type="text"
                  value={formData.assigned_divisions?.join(', ')}
                  onChange={handleDivisionsChange}
                  className="w-full h-10 px-3 rounded-lg border-2 border-slate-300 focus:border-blue-500 focus:ring-0"
                  placeholder="e.g., 10, 11, 12"
                  data-testid="staff-divisions-input"
                />
              </div>
              
              {/* Park Assignment for Supervisors */}
              {formData.category === 'supervisor' && (
                <div>
                  <label className="text-sm font-bold text-slate-700 mb-1 block">Assigned Parks</label>
                  {parksLoading ? (
                    <div className="text-sm text-slate-500">Loading parks...</div>
                  ) : parksList.length > 0 ? (
                    <div className="max-h-48 overflow-y-auto border-2 border-slate-300 rounded-lg p-2">
                      {parksList.map(park => (
                        <label key={park.park_id} className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.assigned_parks?.includes(park.park_id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData({ ...formData, assigned_parks: [...(formData.assigned_parks || []), park.park_id] });
                              } else {
                                setFormData({ ...formData, assigned_parks: (formData.assigned_parks || []).filter(id => id !== park.park_id) });
                              }
                            }}
                            className="w-4 h-4"
                          />
                          <span className="text-sm">{park.park_name}</span>
                          <span className="text-xs text-slate-400">Zone {park.zone}, Div {park.division}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-slate-500 p-2 bg-slate-50 rounded-lg">
                      {formData.assigned_zones?.length > 0 || formData.assigned_divisions?.length > 0 
                        ? 'No parks found for selected zones/divisions'
                        : 'Enter zones/divisions above to see available parks'}
                    </div>
                  )}
                  <div className="mt-2 text-xs flex justify-between">
                    <span className={`font-semibold ${
                      (formData.assigned_parks?.length || 0) > 0 ? 'text-green-600' : 'text-slate-400'
                    }`}>
                      {formData.assigned_parks?.length || 0} of {parksList.length} parks selected
                    </span>
                    {parksList.length > 0 && (
                      <button 
                        type="button"
                        onClick={() => setFormData({ ...formData, assigned_parks: parksList.map(p => p.park_id) })}
                        className="text-blue-600 hover:underline"
                      >
                        Select all
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="p-4 border-t flex gap-3 justify-end flex-shrink-0">
              <button
                onClick={() => { setShowAddModal(false); setShowEditModal(false); }}
                className="h-10 px-6 bg-slate-200 hover:bg-slate-300 rounded-lg font-bold"
              >
                Cancel
              </button>
              <button
                onClick={showAddModal ? handleAddStaff : handleUpdateStaff}
                className="h-10 px-6 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold"
                data-testid="save-staff-btn"
              >
                {showAddModal ? 'Add Staff' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Excel Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="font-bold text-lg">Upload Staff Excel</h2>
              <button onClick={() => setShowUploadModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <p className="text-sm text-slate-600 mb-4">
                Upload an Excel file with columns: <strong>Name</strong>, <strong>Mobile Number</strong>, <strong>Category</strong> (Electrician/Supervisor), Zone, Division, Salary
              </p>
              <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer ${uploading ? 'border-blue-400 bg-blue-50' : 'border-slate-300 hover:border-blue-400 hover:bg-blue-50'}`}>
                {uploading ? (
                  <div className="text-center">
                    <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                    <span className="text-sm text-blue-600">Uploading... {Math.round(uploadProgress)}%</span>
                  </div>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-slate-400 mb-2" />
                    <span className="text-sm text-slate-500">Click to upload Excel file</span>
                  </>
                )}
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleExcelUpload}
                  className="hidden"
                  disabled={uploading}
                  data-testid="excel-upload-input"
                />
              </label>
              
              {uploadResult && (
                <div className={`mt-4 p-4 rounded-lg ${uploadResult.total_errors > 0 ? 'bg-yellow-50 border border-yellow-200' : 'bg-green-50 border border-green-200'}`}>
                  <div className="font-bold mb-2">{uploadResult.message}</div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>Created: <span className="font-bold text-green-600">{uploadResult.created_count}</span></div>
                    <div>Updated: <span className="font-bold text-blue-600">{uploadResult.updated_count}</span></div>
                  </div>
                  {uploadResult.errors?.length > 0 && (
                    <div className="mt-2 text-xs text-red-600">
                      Errors: {uploadResult.errors.slice(0, 3).join(', ')}
                      {uploadResult.errors.length > 3 && ` +${uploadResult.errors.length - 3} more`}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp Upload Modal */}
      {showWhatsAppModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="font-bold text-lg">Upload WhatsApp Attendance</h2>
              <button onClick={() => setShowWhatsAppModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <p className="text-sm text-slate-600 mb-4">
                Export WhatsApp chat as text file and upload. Attendance will be marked as <strong>Present</strong> for staff who sent messages.
              </p>
              
              <div className="mb-4">
                <label className="text-sm font-bold text-slate-700 mb-1 block">Select Region</label>
                <select
                  value={whatsAppRegion}
                  onChange={(e) => setWhatsAppRegion(e.target.value)}
                  className="w-full h-10 px-3 rounded-lg border-2 border-slate-300 focus:border-blue-500 focus:ring-0"
                  data-testid="whatsapp-region-select"
                >
                  <option value="north">North (Zones 1-5)</option>
                  <option value="central">Central (Zones 6-10)</option>
                  <option value="south">South (Zones 11-15)</option>
                </select>
              </div>
              
              <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer ${uploading ? 'border-emerald-400 bg-emerald-50' : 'border-slate-300 hover:border-emerald-400 hover:bg-emerald-50'}`}>
                {uploading ? (
                  <div className="text-center">
                    <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                    <span className="text-sm text-emerald-600">Processing... {Math.round(uploadProgress)}%</span>
                  </div>
                ) : (
                  <>
                    <MessageSquare className="w-8 h-8 text-slate-400 mb-2" />
                    <span className="text-sm text-slate-500">Click to upload WhatsApp .txt file</span>
                  </>
                )}
                <input
                  type="file"
                  accept=".txt"
                  onChange={handleWhatsAppUpload}
                  className="hidden"
                  disabled={uploading}
                  data-testid="whatsapp-upload-input"
                />
              </label>
              
              {uploadResult && (
                <div className={`mt-4 p-4 rounded-lg ${uploadResult.marked_count > 0 ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                  <div className="font-bold mb-2">{uploadResult.message}</div>
                  
                  {/* Summary Stats */}
                  <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                    <div className="bg-white p-2 rounded">
                      <div className="text-xs text-slate-500">NEW Attendance Marked</div>
                      <div className="text-xl font-bold text-green-600">{uploadResult.marked_count}</div>
                    </div>
                    <div className="bg-white p-2 rounded">
                      <div className="text-xs text-slate-500">Already Existed</div>
                      <div className="text-xl font-bold text-slate-500">{uploadResult.skipped_count}</div>
                    </div>
                  </div>
                  
                  {/* Processing Details */}
                  <div className="text-xs text-slate-600 space-y-1 border-t pt-2">
                    <div>📄 Total lines in file: <strong>{uploadResult.total_lines_in_file}</strong></div>
                    <div>📝 Valid message lines: <strong>{uploadResult.parsed_message_lines}</strong></div>
                    <div>📱 Unique phone+date combinations: <strong>{uploadResult.total_messages_parsed}</strong></div>
                  </div>
                  
                  {/* Matched Staff Sample */}
                  {uploadResult.matched_staff_sample?.length > 0 && (
                    <div className="mt-3 p-2 bg-green-100 rounded text-xs">
                      <div className="font-bold text-green-800 mb-1">✓ Recently Marked:</div>
                      <div className="text-green-700">{uploadResult.matched_staff_sample.slice(0, 5).join(', ')}</div>
                    </div>
                  )}
                  
                  {/* Unmatched Phones Warning */}
                  {uploadResult.unmatched_phones_count > 0 && (
                    <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded">
                      <div className="font-bold text-red-700 text-sm mb-1">
                        ⚠ {uploadResult.unmatched_phones_count} phone numbers NOT found in staff database
                      </div>
                      <div className="text-xs text-red-600 mb-1">
                        These messages were ignored because the phone numbers don't match any registered staff:
                      </div>
                      <div className="text-xs text-red-800 font-mono bg-red-100 p-1 rounded">
                        {uploadResult.unmatched_phones_sample?.slice(0, 5).join(', ')}
                        {uploadResult.unmatched_phones_count > 5 && <span className="text-red-500"> +{uploadResult.unmatched_phones_count - 5} more</span>}
                      </div>
                      <div className="text-xs text-red-600 mt-1">
                        → Add these phone numbers to staff records to capture their attendance
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ZoneStaffManagement;
